import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { dateLocale } from '@/lib/locale';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useAuth } from '@/context/AuthContext';
import { useMerchantId } from '@/hooks/useMerchantId';
import { useBranches } from '@/hooks/api/branches';
import { formatUZS } from '@/lib/format';
import { TrendingUp, ShoppingCart, FileText, Package, ArrowRight, Users, MapPin, CreditCard, Wallet, Banknote, TicketCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { FETCH_ALL_MAX_PAGES, FETCH_ALL_PER_PAGE } from '@/lib/fetch-all';

type Sale = components['schemas']['SaleResource'];
type MurabahaCase = components['schemas']['MurabahaCaseResource'];
type Ticket = components['schemas']['TicketResource'];


export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { merchantId } = useMerchantId();

  const { data: sales = [], isLoading } = useQuery({
    queryKey: ['sales', merchantId],
    queryFn: async () => {
      const all: Sale[] = [];
      for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page += 1) {
        const { data, error } = await apiClient.GET('/sales', { params: { query: { page, per_page: FETCH_ALL_PER_PAGE } } });
        if (error) throw error;
        all.push(...(data?.data ?? []));
        if (page >= (data?.meta?.last_page ?? 1)) break;
      }
      return all;
    },
    enabled: !!merchantId,
  });

  const { data: murabahaCases = [] } = useQuery({
    queryKey: ['/murabaha/cases', merchantId],
    queryFn: async () => {
      const all: MurabahaCase[] = [];
      for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page += 1) {
        const { data, error } = await apiClient.GET('/murabaha/cases', { params: { query: { page, per_page: FETCH_ALL_PER_PAGE } as never } });
        if (error) throw error;
        all.push(...(data?.data ?? []));
        if (page >= (data?.meta?.last_page ?? 1)) break;
      }
      return all;
    },
    enabled: !!merchantId,
  });

  const { data: productsData } = useQuery({ queryKey: ['/products'], enabled: !!merchantId });
  const productsCount = productsData?.meta?.total ?? productsData?.data?.length ?? 0;

  const { data: customersData } = useQuery({ queryKey: ['/customers'], enabled: !!merchantId });
  const customersCount = customersData?.meta?.total ?? customersData?.data?.length ?? 0;

  const { data: branches = [] } = useBranches();

  const { data: ticketsData } = useQuery({ queryKey: ['/tickets'], enabled: !!merchantId });
  const tickets = (ticketsData?.data ?? []) as Ticket[];

  const today = new Date().toISOString().slice(0, 10);
  const todaySales = sales.filter(s => s.created_at.slice(0, 10) === today);
  const todayTotal = todaySales.reduce((s, sale) => s + Number(sale.total_amount), 0);
  const totalSales = sales.reduce((s, sale) => s + Number(sale.total_amount), 0);
  const pendingMurabaha = murabahaCases.filter(m => m.status === 'PendingCustomerConfirmation').length;

  const openTickets = tickets.filter((tk) => tk.status === 'new' || tk.status === 'reviewing').length;
  const resolvedTickets = tickets.filter((tk) => tk.status === 'resolved' || tk.status === 'closed').length;

  const cashTotal = sales.filter(s => s.payment_method === 'cash').reduce((s, sale) => s + Number(sale.total_amount), 0);
  const cardTotal = sales.filter(s => s.payment_method === 'card').reduce((s, sale) => s + Number(sale.total_amount), 0);
  const walletTotal = sales.filter(s => s.payment_method === 'hisob_wallet').reduce((s, sale) => s + Number(sale.total_amount), 0);
  const murabahaSalesTotal = sales.filter(s => s.payment_method === 'murabaha').reduce((s, sale) => s + Number(sale.total_amount), 0);

  const mainStats = [
    { label: t('misc_dash.today_sales'), value: formatUZS(todayTotal), icon: TrendingUp, color: 'text-success' },
    { label: t('misc_dash.total_sales'), value: formatUZS(totalSales), icon: ShoppingCart, color: 'text-info' },
    { label: t('misc_dash.pending_murabaha'), value: String(pendingMurabaha), icon: FileText, color: 'text-warning' },
    { label: t('misc_dash.products'), value: String(productsCount), icon: Package, color: 'text-primary' },
  ];

  const secondaryStats = [
    { label: t('misc_dash.customers'), value: String(customersCount), icon: Users, color: 'text-info' },
    { label: t('misc_dash.branches'), value: String(branches.length), icon: MapPin, color: 'text-success' },
    { label: t('misc_dash.open_tickets'), value: String(openTickets), icon: TicketCheck, color: 'text-warning', link: '/tickets' },
    { label: t('misc_dash.resolved'), value: String(resolvedTickets), icon: TicketCheck, color: 'text-success', link: '/tickets' },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-foreground">{t('misc_dash.hello', { name: user?.full_name })}</h1>
        <p className="text-sm text-muted-foreground">{t('misc_dash.subtitle')}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {mainStats.map(stat => (
          <div key={stat.label} className="bg-card rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 mb-2">
              <stat.icon size={16} className={stat.color} />
              <span className="text-xs text-muted-foreground">{stat.label}</span>
            </div>
            <p className="text-lg font-semibold text-foreground">{isLoading ? '...' : stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {secondaryStats.map(stat => {
          const Wrapper = stat.link ? Link : 'div';
          const wrapperProps = stat.link ? { to: stat.link } : {};
          return (
            <Wrapper key={stat.label} {...(wrapperProps as any)} className="bg-card rounded-xl border border-border p-4 hover:border-primary/30 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <stat.icon size={16} className={stat.color} />
                <span className="text-xs text-muted-foreground">{stat.label}</span>
              </div>
              <p className="text-lg font-semibold text-foreground">{isLoading ? '...' : stat.value}</p>
            </Wrapper>
          );
        })}
      </div>

      <h2 className="text-sm font-semibold text-foreground mb-3">{t('misc_dash.by_method')}</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Banknote size={16} className="text-success" />
            <span className="text-xs text-muted-foreground">{t('misc_common.pay_cash')}</span>
          </div>
          <p className="text-lg font-semibold text-foreground">{isLoading ? '...' : formatUZS(cashTotal)}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={16} className="text-info" />
            <span className="text-xs text-muted-foreground">{t('misc_common.pay_card')}</span>
          </div>
          <p className="text-lg font-semibold text-foreground">{isLoading ? '...' : formatUZS(cardTotal)}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <Wallet size={16} className="text-warning" />
            <span className="text-xs text-muted-foreground">{t('misc_dash.wallet')}</span>
          </div>
          <p className="text-lg font-semibold text-foreground">{isLoading ? '...' : formatUZS(walletTotal)}</p>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={16} className="text-primary" />
            <span className="text-xs text-muted-foreground">{t('misc_common.murabaha')}</span>
          </div>
          <p className="text-lg font-semibold text-foreground">{isLoading ? '...' : formatUZS(murabahaSalesTotal)}</p>
        </div>
      </div>

      <h2 className="text-sm font-semibold text-foreground mb-3">{t('misc_dash.recent')}</h2>
      <div className="bg-card border border-border rounded-xl overflow-hidden mb-6">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left text-xs font-medium text-muted-foreground p-3">ID</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_dash.th_amount')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_dash.th_payment')}</th>
              <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('misc_dash.th_date')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t('misc_common.loading')}</td></tr>
            ) : sales.length === 0 ? (
              <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t('misc_dash.sales_empty')}</td></tr>
            ) : (
              [...sales]
                .sort((a, b) => b.created_at.localeCompare(a.created_at))
                .slice(0, 5)
                .map(s => (
                  <tr key={s.id} className="border-b border-border last:border-0">
                    <td className="p-3 text-sm text-muted-foreground font-mono">#{s.id}</td>
                    <td className="p-3 text-sm text-foreground font-medium">{formatUZS(Number(s.total_amount))}</td>
                    <td className="p-3"><Badge variant="secondary" className="text-xs capitalize">{s.payment_method}</Badge></td>
                    <td className="p-3 text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString(dateLocale())}</td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-4">
        <Link to="/pos" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
          <ShoppingCart size={16} /> {t('misc_dash.go_pos')} <ArrowRight size={14} />
        </Link>
        <Link to="/sales" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-secondary text-secondary-foreground text-sm hover:bg-accent transition-colors">
          {t('misc_dash.view_sales')}
        </Link>
      </div>
    </div>
  );
}
